import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { getStrWithoutSpaces } from 'legacy/lut/components/widgets/public/partner360/helpers/getStrWithoutSpaces'

import { TMarketYoyGrowthExpectationMarket } from '../../../api/types'
import {
  IMarketYoyGrowthCategory,
  IMarketYoyGrowthExpectation,
  IMarketYoyGrowthGeoMarket,
} from '../../../model/types'
import {
  IGrowthValues,
  IMarketYoyGrowthExpectationGroup,
  IMarketYoyGrowthExpectationGroupRow,
  IMarketYoyGrowthExpectationItemRow,
  TGrowthValue,
  TGrowthValuesByMarket,
} from '../types'

const getSharedGrowthValue = (values: number[]): TGrowthValue => {
  if (!values.length) {
    return null
  }

  return values.every((value) => value === values[0])
    ? values[0]
    : { min: Math.min(...values), max: Math.max(...values) }
}

const getGrowthValues = (records: IMarketYoyGrowthExpectation[]): IGrowthValues => ({
  yoyGrowth: getSharedGrowthValue(records.map((record) => record.yoyGrowth)),
  sobYoyGrowth: getSharedGrowthValue(records.map((record) => record.sobYoyGrowth)),
})

const getGrowthValuesByMarket = (
  records: IMarketYoyGrowthExpectation[],
  markets: TMarketYoyGrowthExpectationMarket[]
): TGrowthValuesByMarket => {
  const byMarket: TGrowthValuesByMarket = {}

  markets.forEach((market) => {
    const marketRecords = records.filter((record) => record.market === market)

    if (marketRecords.length) {
      byMarket[market] = getGrowthValues(marketRecords)
    }
  })

  return byMarket
}

const buildItemRow = (
  offering: ProgramTypes,
  productCategory: string,
  categoryRecords: IMarketYoyGrowthExpectation[],
  markets: TMarketYoyGrowthExpectationMarket[]
): IMarketYoyGrowthExpectationItemRow => ({
  type: 'item',
  uid: `${offering}-${productCategory}`,
  offering,
  productCategory,
  ww: getGrowthValues(categoryRecords),
  byMarket: getGrowthValuesByMarket(categoryRecords, markets),
})

const getOfferingTranslationKey = (offering: ProgramTypes) =>
  `jbp-partner-type-${getStrWithoutSpaces(offering)}`

const buildGroupRow = (
  offering: ProgramTypes,
  offeringRecords: IMarketYoyGrowthExpectation[],
  markets: TMarketYoyGrowthExpectationMarket[]
): IMarketYoyGrowthExpectationGroupRow => ({
  type: 'group',
  uid: `group-${offering}`,
  offering,
  offeringTranslationKey: getOfferingTranslationKey(offering),
  ww: getGrowthValues(offeringRecords),
  byMarket: getGrowthValuesByMarket(offeringRecords, markets),
})

interface IGetMarketYoyGrowthExpectationGroupsResult {
  groups: IMarketYoyGrowthExpectationGroup[]
  markets: TMarketYoyGrowthExpectationMarket[]
}

/**
 * Groups are always seeded from `categories` (the reference offering/category
 * hierarchy) and `markets` are always seeded from `geoMarkets` (the reference
 * market list), ordered by `orderBy`/`order`, so every known offering,
 * category, and market renders even with zero matching `planningDetails`. Any
 * offering/category/market present in `planningDetails` but absent from the
 * reference lists is appended afterward so real data is never hidden.
 */
export const getMarketYoyGrowthExpectationGroups = (
  planningDetails: IMarketYoyGrowthExpectation[],
  categories: IMarketYoyGrowthCategory[],
  geoMarkets: IMarketYoyGrowthGeoMarket[]
): IGetMarketYoyGrowthExpectationGroupsResult => {
  const recordsByOffering = new Map<ProgramTypes, Map<string, IMarketYoyGrowthExpectation[]>>()

  planningDetails.forEach((record) => {
    const categoryRecordsMap = recordsByOffering.get(record.offering) ?? new Map()
    const categoryRecords = categoryRecordsMap.get(record.productCategory) ?? []
    categoryRecords.push(record)
    categoryRecordsMap.set(record.productCategory, categoryRecords)
    recordsByOffering.set(record.offering, categoryRecordsMap)
  })

  const seenMarkets = new Set<TMarketYoyGrowthExpectationMarket>()
  const markets: TMarketYoyGrowthExpectationMarket[] = []

  ;[...geoMarkets]
    .sort((a, b) => a.order - b.order)
    .forEach((geoMarket) => {
      if (!seenMarkets.has(geoMarket.short)) {
        seenMarkets.add(geoMarket.short)
        markets.push(geoMarket.short)
      }
    })

  planningDetails.forEach((record) => {
    if (!seenMarkets.has(record.market)) {
      seenMarkets.add(record.market)
      markets.push(record.market)
    }
  })

  const categoriesByOffering = new Map<ProgramTypes, IMarketYoyGrowthCategory[]>()

  categories.forEach((category) => {
    const offeringCategories = categoriesByOffering.get(category.offering) ?? []

    if (!offeringCategories.some((existing) => existing.category === category.category)) {
      offeringCategories.push(category)
    }

    categoriesByOffering.set(category.offering, offeringCategories)
  })

  // Offerings are ordered by the minimum `orderBy` of their categories rather
  // than by first-appearance in `categories`, since the API doesn't guarantee
  // row order — `min` is the only deterministic, order-independent choice.
  const orderedOfferings = Array.from(categoriesByOffering.entries())
    .map(([offering, offeringCategories]) => ({
      offering,
      orderedCategories: [...offeringCategories].sort((a, b) => a.orderBy - b.orderBy),
      rank: Math.min(...offeringCategories.map((category) => category.orderBy)),
    }))
    .sort((a, b) => a.rank - b.rank)

  const consumedOfferings = new Set<ProgramTypes>()

  const seededGroups = orderedOfferings.map(({ offering, orderedCategories }) => {
    consumedOfferings.add(offering)

    const categoryRecordsMap =
      recordsByOffering.get(offering) ?? new Map<string, IMarketYoyGrowthExpectation[]>()
    const consumedCategories = new Set<string>()

    const items = orderedCategories.map((category) => {
      consumedCategories.add(category.category)

      return buildItemRow(
        offering,
        category.category,
        categoryRecordsMap.get(category.category) ?? [],
        markets
      )
    })

    Array.from(categoryRecordsMap.entries())
      .filter(([productCategory]) => !consumedCategories.has(productCategory))
      .forEach(([productCategory, categoryRecords]) => {
        items.push(buildItemRow(offering, productCategory, categoryRecords, markets))
      })

    const offeringRecords = Array.from(categoryRecordsMap.values()).flat()

    return { group: buildGroupRow(offering, offeringRecords, markets), items }
  })

  const extraGroups = Array.from(recordsByOffering.entries())
    .filter(([offering]) => !consumedOfferings.has(offering))
    .map(([offering, categoryRecordsMap]) => {
      const items = Array.from(categoryRecordsMap.entries()).map(
        ([productCategory, categoryRecords]) =>
          buildItemRow(offering, productCategory, categoryRecords, markets)
      )
      const offeringRecords = Array.from(categoryRecordsMap.values()).flat()

      return { group: buildGroupRow(offering, offeringRecords, markets), items }
    })

  const groups = [...seededGroups, ...extraGroups]

  return { groups, markets }
}
